import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { CheckCircle2, AlertTriangle, Info, XCircle, X } from 'lucide-react'
import { cn } from '../../utils/cn'

export type ToastTone = 'success' | 'error' | 'warning' | 'info'

export interface ToastOptions {
  title?: ReactNode
  message?: ReactNode
  tone?: ToastTone
  /** ms; 0 = sticky until dismissed */
  duration?: number
  action?: { label: string; onClick: () => void }
}

interface ToastItem extends ToastOptions { id: number }

interface ToastApi {
  toast: (opts: ToastOptions | string) => number
  success: (message: ReactNode, title?: ReactNode) => number
  error: (message: ReactNode, title?: ReactNode) => number
  warning: (message: ReactNode, title?: ReactNode) => number
  info: (message: ReactNode, title?: ReactNode) => number
  dismiss: (id: number) => void
}

const ToastContext = createContext<ToastApi | null>(null)

// Module-level bridge so non-React code (api interceptors, stores) can toast.
let externalToast: ToastApi['toast'] | null = null
export function toastFromAnywhere(opts: ToastOptions | string) { externalToast?.(opts) }

const ICON: Record<ToastTone, ReactNode> = {
  success: <CheckCircle2 className="w-5 h-5 text-status-success" aria-hidden />,
  error: <XCircle className="w-5 h-5 text-status-danger" aria-hidden />,
  warning: <AlertTriangle className="w-5 h-5 text-status-warning" aria-hidden />,
  info: <Info className="w-5 h-5 text-status-info" aria-hidden />,
}

const BORDER: Record<ToastTone, string> = {
  success: 'border-l-status-success',
  error: 'border-l-status-danger',
  warning: 'border-l-status-warning',
  info: 'border-l-status-info',
}

export function ToastProvider({ children }: { children: ReactNode }) {
  const [items, setItems] = useState<ToastItem[]>([])
  const seq = useRef(0)

  const dismiss = useCallback((id: number) => setItems(list => list.filter(t => t.id !== id)), [])

  const toast = useCallback((opts: ToastOptions | string) => {
    const o = typeof opts === 'string' ? { message: opts } : opts
    const id = ++seq.current
    setItems(list => [...list.slice(-4), { id, tone: 'info', duration: 5000, ...o }])
    return id
  }, [])

  const api = useMemo<ToastApi>(() => ({
    toast,
    success: (message, title) => toast({ message, title, tone: 'success' }),
    error: (message, title) => toast({ message, title, tone: 'error', duration: 8000 }),
    warning: (message, title) => toast({ message, title, tone: 'warning' }),
    info: (message, title) => toast({ message, title, tone: 'info' }),
    dismiss,
  }), [toast, dismiss])

  useEffect(() => { externalToast = toast; return () => { externalToast = null } }, [toast])

  return (
    <ToastContext.Provider value={api}>
      {children}
      {typeof document !== 'undefined' && createPortal(
        <div className="fixed bottom-4 right-4 z-[100] flex flex-col gap-2 w-[calc(100vw-2rem)] max-w-sm pointer-events-none" aria-live="polite" aria-relevant="additions">
          {items.map(t => <ToastCard key={t.id} item={t} onDismiss={() => dismiss(t.id)} />)}
        </div>,
        document.body,
      )}
    </ToastContext.Provider>
  )
}

function ToastCard({ item, onDismiss }: { item: ToastItem; onDismiss: () => void }) {
  const tone = item.tone ?? 'info'
  useEffect(() => {
    if (!item.duration) return
    const t = setTimeout(onDismiss, item.duration)
    return () => clearTimeout(t)
  }, [item.duration, onDismiss])
  return (
    <div
      role={tone === 'error' ? 'alert' : 'status'}
      className={cn('pointer-events-auto bg-surface border border-line border-l-4 rounded-lg shadow-lg p-3 flex gap-3 animate-toast-in', BORDER[tone])}
    >
      <div className="shrink-0 mt-0.5">{ICON[tone]}</div>
      <div className="min-w-0 flex-1 text-sm">
        {item.title && <p className="font-semibold text-fg">{item.title}</p>}
        {item.message && <p className="text-fg-muted break-words">{item.message}</p>}
        {item.action && (
          <button type="button" onClick={() => { item.action?.onClick(); onDismiss() }} className="mt-1 text-xs font-medium text-brand-700 hover:underline focus-ring rounded">
            {item.action.label}
          </button>
        )}
      </div>
      <button type="button" onClick={onDismiss} aria-label="Dismiss" className="shrink-0 text-fg-subtle hover:text-fg rounded focus-ring h-6 w-6 flex items-center justify-center">
        <X className="w-4 h-4" />
      </button>
    </div>
  )
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext)
  if (!ctx) {
    // No provider (tests) — degrade to console so callers never crash.
    const noop = (o: ToastOptions | string) => { console.info('[toast]', o); return 0 }
    return { toast: noop, success: (m) => noop({ message: m }), error: (m) => noop({ message: m }), warning: (m) => noop({ message: m }), info: (m) => noop({ message: m }), dismiss: () => {} }
  }
  return ctx
}

/** Extract a human-readable message from an axios/fetch error. */
export function errorMessage(err: unknown, fallback = 'Something went wrong. Please try again.'): string {
  const e = err as { response?: { data?: { detail?: unknown; message?: string } }; message?: string } | null
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0] as { msg?: string }
    if (first?.msg) return first.msg
  }
  if (e?.response?.data?.message) return e.response.data.message
  if (e?.message) return e.message
  return fallback
}
